import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface BookingConfirmationDialogProps {
  data: any
  onSave?: (updatedData: any, changes?: any[]) => void
  shipmentId: string
  documentId: string
}

const bookingConfirmationSections: DocumentSection[] = [
  {
    id: 'documentInfo',
    title: 'Document Information',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Booking Number', path: 'bookingNumber' },
      { type: 'field', label: 'Reference Number', path: 'referenceNumber' },
      { type: 'field', label: 'Booking Date', path: 'bookingDate' },
      { type: 'field', label: 'Confirmation Date', path: 'confirmationDate' },
    ],
  },
  {
    id: 'parties',
    title: 'Parties',
    layout: 'single',
    fields: [
      {
        type: 'object',
        label: 'Shipper',
        path: 'shipper',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Account Number', path: 'accountNumber' },
        ],
      },
      {
        type: 'object',
        label: 'Consignee',
        path: 'consignee',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Account Number', path: 'accountNumber' },
        ],
      },
      {
        type: 'object',
        label: 'Forwarder',
        path: 'forwarder',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Account Number', path: 'accountNumber' },
        ],
      },
      {
        type: 'object',
        label: 'Carrier',
        path: 'carrier',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'SCAC', path: 'scac' },
        ],
      },
      {
        type: 'object',
        label: 'Notify Party',
        path: 'notifyParty',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
        ],
      },
    ],
  },
  {
    id: 'vesselInfo',
    title: 'Vessel Information',
    layout: 'double',
    fields: [
      {
        type: 'object',
        label: 'Vessel',
        path: 'vessel',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Voyage', path: 'voyage' },
          { label: 'Flag', path: 'flag' },
        ],
      },
      { type: 'field', label: 'Voyage', path: 'voyage' },
      { type: 'field', label: 'Service', path: 'service' },
      { type: 'field', label: 'Departure Date', path: 'departureDate' },
      { type: 'field', label: 'Estimated Arrival', path: 'estimatedArrival' },
    ],
  },
  {
    id: 'locations',
    title: 'Locations',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Port of Loading', path: 'portOfLoading' },
      { type: 'field', label: 'Port of Discharge', path: 'portOfDischarge' },
      { type: 'field', label: 'Place of Receipt', path: 'placeOfReceipt' },
      { type: 'field', label: 'Place of Delivery', path: 'placeOfDelivery' },
      { type: 'field', label: 'Final Destination', path: 'finalDestination' },
      {
        type: 'array',
        label: 'Transshipment Ports',
        path: 'transshipmentPorts',
        columns: [{ label: 'Port', path: '.' }],
      },
    ],
  },
  {
    id: 'cutoffDates',
    title: 'Cutoff Dates',
    layout: 'double',
    fields: [
      {
        type: 'object',
        label: 'Cutoff Dates',
        path: 'cutoffDates',
        fields: [
          { label: 'Documentation', path: 'documentation' },
          { label: 'VGM', path: 'vgm' },
          { label: 'Cargo', path: 'cargo' },
          { label: 'Hazardous', path: 'hazardous' },
          { label: 'Reefer', path: 'reefer' },
        ],
      },
    ],
  },
  {
    id: 'cargoDetails',
    title: 'Cargo Details',
    layout: 'single',
    fields: [
      {
        type: 'object',
        label: 'Cargo Details',
        path: 'cargoDetails',
        fields: [
          { label: 'Commodity Description', path: 'commodityDescription' },
          { label: 'Package Type', path: 'packageType' },
          { label: 'Package Count', path: 'packageCount' },
          { label: 'Weight', path: 'weight' },
          { label: 'Volume', path: 'volume' },
          { label: 'Dimensions', path: 'dimensions' },
          { label: 'Hazardous', path: 'hazardous' },
          { label: 'Special Handling', path: 'specialHandling' },
        ],
      },
      {
        type: 'object',
        label: 'Hazardous Details',
        path: 'cargoDetails.hazardousDetails',
        fields: [
          { label: 'UN Number', path: 'unNumber' },
          { label: 'Class', path: 'class' },
          { label: 'Proper Shipping Name', path: 'properShippingName' },
          { label: 'Packing Group', path: 'packingGroup' },
        ],
      },
      {
        type: 'object',
        label: 'Temperature Requirements',
        path: 'cargoDetails.temperature',
        fields: [
          { label: 'Min Temperature', path: 'min' },
          { label: 'Max Temperature', path: 'max' },
          { label: 'Unit', path: 'unit' },
        ],
      },
    ],
  },
  {
    id: 'containers',
    title: 'Containers',
    layout: 'single',
    fields: [
      {
        type: 'array',
        label: 'Containers',
        path: 'containers',
        columns: [
          { label: 'Container Number', path: 'containerNumber' },
          { label: 'Type', path: 'type' },
          { label: 'Size', path: 'size' },
          { label: 'Seal Number', path: 'sealNumber' },
          { label: 'Weight', path: 'weight', complexType: 'weight' },
          { label: 'Volume', path: 'volume', complexType: 'volume' },
          { label: 'Package Count', path: 'packageCount' },
          { label: 'Commodity', path: 'commodityDescription' },
        ],
      },
    ],
  },
  {
    id: 'charges',
    title: 'Charges and Rates',
    layout: 'double',
    fields: [
      {
        type: 'object',
        label: 'Charges',
        path: 'charges',
        fields: [
          { label: 'Freight Rate', path: 'freightRate' },
          { label: 'Freight Charges', path: 'freightCharges' },
          { label: 'Origin Charges', path: 'originCharges' },
          { label: 'Destination Charges', path: 'destinationCharges' },
          { label: 'Total Charges', path: 'totalCharges' },
        ],
      },
      { type: 'field', label: 'Payment Terms', path: 'paymentTerms' },
      { type: 'field', label: 'Incoterms', path: 'incoterms' },
    ],
  },
  {
    id: 'specialInstructions',
    title: 'Special Instructions',
    layout: 'single',
    fields: [
      { type: 'field', label: 'Special Instructions', path: 'specialInstructions', fullWidth: true },
      { type: 'field', label: 'Terms and Conditions', path: 'termsAndConditions', fullWidth: true },
    ],
  },
]

const BookingConfirmationDialog: React.FC<BookingConfirmationDialogProps> = ({
  data,
  onSave,
  shipmentId,
  documentId,
}) => {
  return (
    <DocumentDialog
      documentData={data}
      schema={{}} // TODO: Import bookingConfirmationSchema from backend
      sections={bookingConfirmationSections}
      documentType="Booking Confirmation"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default BookingConfirmationDialog